import { useEffect, useState } from 'react'
import { useI18n } from '../../i18n'
import { buildCloudRequestUrl } from './gcpDiscoverySession.mjs'
import { getActiveOrgId } from '../../utils/orgUtils'

type SourceRow = {
  id?: string
  sourceType: string
  status?: string
  lastObservedAt?: string | null
  lastError?: string | null
  coverage?: { complete?: boolean; limitations?: string[] }
  privateEndpoint?: boolean
  permission?: { permission?: string } | null
  connected?: boolean
}

type Props = {
  token: string
  orgId: string
  integrationId: string
}

export default function GcpSourcesPanel({ token, orgId, integrationId }: Props) {
  const { t } = useI18n()
  const [sources, setSources] = useState<SourceRow[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    void fetch(buildCloudRequestUrl(import.meta.env.VITE_API_URL || '/api', `/${integrationId}/sources`, orgId), {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async (response) => {
      const body = await response.json().catch(() => ({}))
      if (!active || getActiveOrgId() !== orgId) return
      if (!response.ok) {
        setSources([])
        setError(t('gcpSources.unavailable', 'Source status is not available.'))
        return
      }
      setSources(Array.isArray(body.sources) ? body.sources : [])
      setError(null)
    }).catch(() => {
      if (!active) return
      setSources([])
      setError(t('gcpSources.unavailable', 'Source status is not available.'))
    })
    return () => {
      active = false
    }
  }, [integrationId, orgId, t, token])

  return (
    <section data-testid="gcp-sources">
      <h3>{t('gcpSources.title', 'Sources')}</h3>
      <p>{t('gcpSources.intro', 'Compute inventory, OS Config, the host collector, and Kubernetes are separate sources. A missing source is a coverage gap.')}</p>
      {error ? <p>{error}</p> : null}
      <table>
        <thead>
          <tr>
            <th>{t('gcpSources.source', 'Source')}</th>
            <th>{t('gcpSources.status', 'Status')}</th>
            <th>{t('gcpSources.observed', 'Last observation')}</th>
            <th>{t('gcpSources.coverage', 'Coverage')}</th>
            <th>{t('gcpSources.error', 'Error')}</th>
          </tr>
        </thead>
        <tbody>
          {sources.map((row) => (
            <tr key={row.id || row.sourceType}>
              <td>{row.sourceType}</td>
              <td>{row.status === 'NOT_CONNECTED' ? t('gcpSources.notConnected', 'Not connected') : (row.status || t('gcpSources.notConfigured', 'Not configured'))}</td>
              <td>{row.lastObservedAt || t('gcpSources.none', 'None')}</td>
              <td>
                {row.status === 'NOT_CONNECTED'
                  ? t('gcpSources.notConnected', 'Not connected')
                  : row.coverage?.complete
                    ? t('gcpSources.complete', 'Complete')
                    : t('gcpSources.gap', 'Coverage gap')}
                {row.coverage?.limitations?.length ? ` · ${row.coverage.limitations.join(', ')}` : ''}
                {row.permission?.permission ? ` · ${row.permission.permission}` : ''}
                {row.privateEndpoint ? ` · ${t('gcpSources.privateEndpoint', 'Private endpoint')}` : ''}
              </td>
              <td>{row.lastError || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div>
        <button type="button" onClick={() => void fetch(buildCloudRequestUrl(import.meta.env.VITE_API_URL || '/api', `/${integrationId}/sources/os-config`, orgId), { method: 'POST', headers: { Authorization: `Bearer ${token}` } }).then(() => window.location.reload())}>{t('gcpSources.enableOsConfig', 'Enable OS Config')}</button>
        <button type="button" onClick={() => void fetch(buildCloudRequestUrl(import.meta.env.VITE_API_URL || '/api', `/${integrationId}/sources/gke`, orgId), { method: 'POST', headers: { Authorization: `Bearer ${token}` } }).then(() => window.location.reload())}>{t('gcpSources.enableGke', 'Enable GKE')}</button>
        {sources.filter((row) => row.id && row.sourceType !== 'COMPUTE_IAM').map((row) => (
          <button key={row.id} type="button" onClick={() => void fetch(buildCloudRequestUrl(import.meta.env.VITE_API_URL || '/api', `/${integrationId}/sources/${row.id}/collect`, orgId), { method: 'POST', headers: { Authorization: `Bearer ${token}` } })}>{t('gcpSources.collect', 'Collect')} {row.sourceType}</button>
        ))}
      </div>
      <p>{t('gcpSources.install', 'Host collector enrollment, rotation, and the systemd timer are documented with the release notes. The platform does not install the collector. Identity is the administrator binding, not a verified instance token.')}</p>
    </section>
  )
}
